import { authenticatedFetch } from "@/api/session";
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ??
  "http://localhost:8000";

export type ImportFieldDefinition = {
  field: string;
  label: string;
  required: boolean;
};

export type ImportColumnMapping = {
  source_column: string;
  suggested_field: string | null;
};

export type ImportRowIssue = {
  field: string | null;
  message: string;
};

export type ImportPreviewRow = {
  row_number: number;

  status:
    | "READY"
    | "WARNING"
    | "INVALID"
    | "DUPLICATE";

  values: Record<string, unknown>;
  issues: ImportRowIssue[];

  duplicate: DuplicateMatch | null;
};

export type MemberImportPreview = {
  file_name: string;
  total_rows: number;
  ready_rows: number;
  warning_rows: number;
  invalid_rows: number;
  duplicate_rows: number;

  fields: ImportFieldDefinition[];
  mappings: ImportColumnMapping[];
  rows: ImportPreviewRow[];
};

export type MemberImportMappings = Record<
  string,
  string | null
>;

function getAuthToken() {
  return localStorage.getItem("access_token");
}

async function parseImportResponse(
  response: Response
): Promise<MemberImportPreview> {
  if (!response.ok) {
    const body = await response.json().catch(() => null);

    const detail =
      body && typeof body.detail === "string"
        ? body.detail
        : "Failed to preview member import";

    throw new Error(detail);
  }

  return response.json() as Promise<MemberImportPreview>;
}

export async function previewMemberImport(
  churchId: string,
  file: File
): Promise<MemberImportPreview> {
  const formData = new FormData();

  formData.append("file", file);

  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/migration/members/preview`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      body: formData,
    }
  );

  return parseImportResponse(response);
}

export async function previewMappedMemberImport(
  churchId: string,
  file: File,
  mappings: MemberImportMappings
): Promise<MemberImportPreview> {
  const formData = new FormData();

  formData.append("file", file);
  formData.append(
    "mappings",
    JSON.stringify(mappings)
  );

  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/migration/members/preview-mapped`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      body: formData,
    }
  );

  return parseImportResponse(response);
}

export type DuplicateMatch = {
  match_type: "EXACT" | "LIKELY" | "POSSIBLE";
  confidence: number;

  existing_member_id: string;
  existing_member_name: string;

  existing_email: string | null;
  existing_phone: string | null;
  existing_household_id: string | null;
};

export type MemberImportOptions = {
  import_warning_rows: boolean;
  create_households: boolean;
};

export type MemberImportResult = {
  file_name: string;

  members_imported: number;
  households_created: number;

  duplicates_skipped: number;
  invalid_rows_skipped: number;
  warning_rows_skipped: number;

  duration_ms: number;
};

export async function importMembers(
  churchId: string,
  file: File,
  mappings: MemberImportMappings,
  options: MemberImportOptions
): Promise<MemberImportResult> {
  const formData = new FormData();

  formData.append("file", file);

  formData.append(
    "mappings",
    JSON.stringify(mappings)
  );

  formData.append(
    "import_warning_rows",
    String(options.import_warning_rows)
  );

  formData.append(
    "create_households",
    String(options.create_households)
  );

  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/churches/${churchId}/migration/members/import`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    const body = await response
      .json()
      .catch(() => null);

    const detail =
      body && typeof body.detail === "string"
        ? body.detail
        : "Failed to import members";

    throw new Error(detail);
  }

  return response.json() as Promise<MemberImportResult>;
}